// @flow

import * as React from 'react'
import type { ComponentType } from 'react'

import styled from '@emotion/styled'
import { text, Icon } from '@toggl/ui'
import { media, colors, lineHeights } from '@toggl/style'
import type { GatsbyLinkProps } from 'gatsby'

const Icons = {
  track: <Icon.Track />,
  hire: <Icon.Hire />,
  plan: <Icon.Plan />,
  global: <Icon.Global />,
}

type JobType = {
  title: string,
  position: string,
  description: string,
  team: 'track' | 'hire' | 'plan' | 'global',
  slug: string,
}

type Props = {
  title: string,
  jobs: Array<JobType>,
  Link: React.ComponentType<GatsbyLinkProps<any>>,
}
export const ThreeCol = ({ title, jobs, Link }: Props) => {
  const count = jobs.length
  const summaryText = `${count} position${count === 1 ? '' : 's'}`

  return (
    <Root>
      <HeaderWrapper>
        <Summary>{summaryText}</Summary>
        <Title>{title}</Title>
      </HeaderWrapper>

      {jobs.map((vacancy, id) => (
        <Link to={`/jobs/${vacancy.slug}`} key={id}>
          <VacancyWrapper>
            <VacancyTitleWrapper>
              <VacancyTitle>{vacancy.title}</VacancyTitle>
              {vacancy.position}
            </VacancyTitleWrapper>

            <VacancyDescription>{vacancy.description}</VacancyDescription>

            <TeamStyled>
              <div>{Icons[vacancy.team]}</div>
            </TeamStyled>
          </VacancyWrapper>
        </Link>
      ))}
    </Root>
  )
}

const TeamStyled: ComponentType<any> = styled.div`
  margin-top: 33px;

  svg {
    height: 28px;
  }

  ${media.mq[1]} {
    margin-top: 0;
    flex: 0 0 140px;
  }

  ${media.mq[2]} {
    flex: 0 0 200px;
  }
`

const VacancyDescription: ComponentType<any> = styled.div`
  display: none;

  ${media.mq[1]} {
    display: block;
    flex: 2;
    margin: 0 25px;
  }
  ${media.mq[2]} {
    margin: 0 50px;
  }
`

const VacancyTitleWrapper: ComponentType<any> = styled.div`
  ${media.mq[1]} {
    flex: 1;
  }
`

const VacancyTitle: ComponentType<any> = styled.div`
  ${text.paragraph1};
  margin-bottom: 7px;

  ${media.mq[1]} {
    ${text.paragraph1};
  }
`

const VacancyWrapper: ComponentType<any> = styled.div`
  ${text.paragraph4}
  display: flex;
  flex-direction: column;
  padding: 30px;
  margin: 10px 0;
  background-color: ${colors.darkPurple};
  color: ${colors.beige};

  ${media.mq[1]} {
    margin: 15px 0;
    flex-direction: row;
    justify-content: space-between;
    &:hover {
      background-color: ${colors.fadedPurpleAlt};
    }
  }
  ${media.mq[2]} {
    padding: 45px 55px;
  }
`

const HeaderWrapper: ComponentType<any> = styled.div`
  ${text.heading3}
  margin: 0 0 25px;

  ${media.mq[2]} {
    margin-bottom: 45px;
  }
`

const Title: ComponentType<any> = styled.div`
  ${text.heading3}
  color: ${colors.yellow};
  line-height: ${lineHeights.heading1};

  ${media.mq[1]} {
    display: flex;
  }
`
const Summary: ComponentType<any> = styled.div`
  ${text.heading5}
  color: ${colors.beige};
  margin-bottom: 6px;

  ${media.mq[1]} {
   ${text.paragraph1}
    margin-bottom: 0;
    display: flex;
  }
`

const Root: ComponentType<any> = styled.div`
  padding: 10px 30px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 20px;
  ${media.mq[2]} {
    padding: 30px;
  }
`
